import { GiphyFetch, Rating } from '@giphy/js-fetch-api'
import { IGif } from '@giphy/js-types'
import { tabletQuery } from '@globals/themes/themeConstants'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import useContainerDimensions from '@shared/hooks/useContainerDimensions'
import DebouncedSearchInput from '@shared/search/DebouncedSearchInput'
import { useMutation } from '@tanstack/react-query'
import { FC, Suspense, lazy, useEffect, useRef, useState } from 'react'
import { useDebounce } from 'use-debounce'
import useGiphyDialog from './useGiphyDialog'

const Grid = lazy(async () => {
  const mod = await import('@giphy/react-components')
  return { default: mod.Grid }
})

const gridContainerStyle: SxProps = {
  overflowY: 'scroll',
  '& .giphy-grid': {
    maxHeight: 400,
  },
  // Prevent link navigation while keeping links clickable for Grid's handler
  '& a[href*="giphy.com"]': {
    '&:hover': {
      cursor: 'pointer',
    },
  },
}

type FetchOptions = {
  offset: number
  limit: number
  rating?: Rating
}

type GiphySearchContentProps = {
  handleGifClick: (gif: IGif, e: React.SyntheticEvent<HTMLElement, Event>) => void
  id: string
}

const GiphySearchContent: FC<GiphySearchContentProps> = ({ handleGifClick, id }) => {
  const isTablet = useMediaQuery(tabletQuery)

  const [searchValue, setSearchValue] = useState<string>('')
  const [debouncedSearchValue] = useDebounce(searchValue, 300)

  const containerRef = useRef<HTMLDivElement>(null)
  const dimensions = useContainerDimensions(containerRef)

  const { giphyApiKey } = useGiphyDialog(id)

  const giphyApi = new GiphyFetch(giphyApiKey)

  // Leaving `isPending` as `_isFetchSearchPending` and `_isFetchTrendingPending` placeholders since giphy will handle calling these
  // internally.
  const { mutateAsync: fetchSearch, isPending: _isFetchSearchPending } = useMutation({
    mutationFn: ({ searchValue, options }: { searchValue: string; options: FetchOptions }) =>
      giphyApi.search(searchValue, options),
    retry: 3,
  })

  const { mutateAsync: fetchTrending, isPending: _isFetchTrendingPending } = useMutation({
    mutationFn: ({ options }: { options: FetchOptions }) => giphyApi.trending(options),
    retry: 3,
  })

  const giphyFetchFn = (offset: number) => {
    const fetchOptions: FetchOptions = { offset, limit: 10, rating: 'g' }

    if (debouncedSearchValue) {
      return fetchSearch({ searchValue: debouncedSearchValue, options: fetchOptions })
    } else {
      return fetchTrending({ options: fetchOptions })
    }
  }

  const handleSearchInputChange = (searchText: string) => {
    setSearchValue(searchText)
  }

  // Prevent link navigation by changing href to javascript:void(0)
  // This keeps links clickable for React handlers while preventing navigation
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const modifyLinks = () => {
      const links = container.querySelectorAll('a[href*="giphy.com"]')
      links.forEach(link => {
        const href = link.getAttribute('href')
        // Only modify if it's a real Giphy URL and hasn't been modified yet
        if (href && href !== 'javascript:void(0)' && !link.hasAttribute('data-giphy-modified')) {
          link.setAttribute('data-href', href) // Store original
          link.setAttribute('href', 'javascript:void(0)')
          link.setAttribute('data-giphy-modified', 'true')
        }
      })
    }

    // Use MutationObserver to watch for new links added by the Grid component
    const observer = new MutationObserver(() => {
      modifyLinks()
    })

    observer.observe(container, {
      childList: true,
      subtree: true,
    })

    // Modify links immediately in case they're already rendered
    modifyLinks()

    return () => {
      observer.disconnect()
    }
  }, [debouncedSearchValue]) // Re-run when search changes (new GIFs loaded)

  return (
    <Stack mt={2} spacing={2}>
      <DebouncedSearchInput elementId="giphySearchInput" onChange={handleSearchInputChange} />
      <Box id="giphyGridContainer" height={400} ref={containerRef} sx={gridContainerStyle}>
        <Suspense fallback={null}>
          <Grid
            columns={isTablet ? 3 : 5}
            fetchGifs={giphyFetchFn}
            gutter={8}
            hideAttribution
            key={debouncedSearchValue}
            onGifClick={handleGifClick}
            width={dimensions.width}
          />
        </Suspense>
      </Box>
    </Stack>
  )
}

export default GiphySearchContent
